import Icon, { IconType } from 'components/Icon';
import { Loader } from 'components/Widget/Loader';
import MarkdownText from 'components/Widget/MarkdownText';
import Thumbnail, { ThumbnailSize } from 'components/Widget/Thumbnail';
import { getBoundingBox } from 'dragonmouse-shared/geometry';
import {
	ChatEntry,
	ChatEntryRestriction,
	ChatStatus,
	FRAME_GAP,
	UserChatEntry,
} from 'dragonmouse-shared/interfaces';
import { useEventListener } from 'events/EventManager';
import { isEqual } from 'lodash';
import { MenuItemAction } from 'menu/MenuItem';
import { useCallback, useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectActiveSceneChat, selectActiveScene, selectActiveStyle } from 'redux/db/reducers';
import {
	canEdit,
	ChatMessageFramesPart,
	ChatMessageTextPart,
	getFrameBounds,
	getFrames,
	parseFramesXml,
	splitMessageWithFrames,
} from 'redux/db/util';
import { centerCanvasAroundSceneBoundingBox, showContextMenu } from 'redux/mode/actions';
import { selectIsMobile } from 'redux/mode/reducers';
import { displayViewOnlyModal } from 'redux/mode/util';
import { RootReducer } from 'redux/reducers';
import { onFileDropped } from 'utils';
import { useTheme } from 'styled-components';
import { useFileUploadCallbacks } from 'hooks/useFileUploadCallbacks';
import { useChatScroll } from '../../hooks/useChatScroll';
import SceneChatInput from '../../ChatInput/SceneChatInput';
import UserMessage from '../UserMessage';
import * as S from './styles';

const SceneConversation = () => {
	const containerRef = useRef<HTMLDivElement>(null);

	// spacer div we will use to force user's last input to scroll to the top of their screen when they submit a prompt
	const spacerRef = useRef<HTMLDivElement>(null);

	const dispatch = useDispatch();
	const theme = useTheme();
	const [focusedFrameIds, setFocusedFrameIds] = useState<string[]>([]);
	const [isDraggingFile, setIsDraggingFile] = useState(false);

	const { activeChat, centerFramesAsChatGeneratesThem, scene, isMobile } = useSelector(
		(r: RootReducer) => {
			const chat = selectActiveSceneChat(r.db, r.mode.activeSceneChatId);
			return {
				activeChat: chat,
				centerFramesAsChatGeneratesThem: r.mode.centerFramesAsChatGeneratesThem,
				activeStyle: selectActiveStyle(r.db, r.mode.activeSceneId),
				authUser: r.mode.authUser,
				scene: selectActiveScene(r.db, r.mode.activeSceneId),
				isMobile: selectIsMobile(r.mode),
			};
		},
		shallowEqual
	);

	const lastChatMessage =
		activeChat?.entries && activeChat.entries.length > 0
			? activeChat.entries[activeChat.entries.length - 1].message
			: null;

	//------------------------------------------------------------------------------------------------
	// HOOKS
	//------------------------------------------------------------------------------------------------

	// Use shared scroll hook
	const { isScrolledToBottom, onJumpToBottom } = useChatScroll({
		containerRef,
		spacerRef,
		entries: activeChat?.entries || [],
		activeChatId: activeChat?.id || null,
		lastChatMessage,
		shouldSkipAutoScroll: () => {
			// Skip auto-scroll for X conversations
			return !!(scene?.metadata && scene.metadata.xConversationId);
		},
		isInitialized: !!scene?.metadata,
	});

	// When MenuItem emits scroll-chat-to-frame, scroll to the thumbnail with that frameId
	useEventListener(
		'scroll-chat-to-frame',
		useCallback((frameId: string) => {
			const tryScroll = (retries: number) => {
				if (!containerRef.current) return;
				const frameElement = containerRef.current.querySelector(`[data-frame-id="${frameId}"]`);
				if (frameElement) {
					const frameOffset = (frameElement as HTMLElement).offsetTop;
					const containerHeight = containerRef.current.clientHeight;
					containerRef.current.style.scrollBehavior = 'smooth';
					containerRef.current.scrollTop = frameOffset - containerHeight / 2;
				} else if (retries > 0) {
					requestAnimationFrame(() => tryScroll(retries - 1));
				}
			};
			tryScroll(10);
		}, [])
	);

	// When comment navigation emits scroll-chat-to-position, restore scroll position
	useEventListener(
		'scroll-chat-to-position',
		useCallback((position: { scrollTop: number; scrollHeight: number }) => {
			if (!containerRef.current) return;
			const currentHeight = containerRef.current.scrollHeight;

			let targetTop: number;
			if (!position.scrollHeight || Math.abs(currentHeight - position.scrollHeight) < 2) {
				targetTop = position.scrollTop;
			} else {
				targetTop = (position.scrollTop / position.scrollHeight) * currentHeight;
			}

			if (!Number.isFinite(targetTop)) return;
			containerRef.current.style.scrollBehavior = 'smooth';
			containerRef.current.scrollTop = targetTop;
		}, [])
	);

	// Add new useEffect hook to handle lastChatMessage changes
	useEffect(() => {
		if (!lastChatMessage) return;
		if (!centerFramesAsChatGeneratesThem) return;

		// Get the last <frames> block from the message
		const frameRegex = /<frames>[\s\S]*?<\/frames>/g;
		const matches = lastChatMessage.match(frameRegex);
		if (!matches) return;

		const lastFramesBlock = matches[matches.length - 1];
		const frameIds = parseFramesXml(lastFramesBlock);

		// Compare with current focusedFrameIds
		if (!isEqual(frameIds.sort(), focusedFrameIds.sort())) {
			setFocusedFrameIds(frameIds);

			// Get the frames and calculate their bounding box
			const frames = getFrames(frameIds);
			if (frames.length > 0) {
				// We have a bit of a glitch here. solving it the right way is tricky
				// In practice what happens is that we zoom into just the first frame, instead of the whole set
				//
				// A small hack does a great job of fixing it (for now)
				// The problem is that the frames aren't in the db slice yet when the lastChatMessage updates.
				// Therefore frames.length !== frameIds.length
				// and the when we try to take the bounds of frames, we just get some of them,
				// so we zoom into some (not all) of the frames, in practice just the first one
				//
				// The fix is to assume (as is currently always the case) that the frames are the same size,
				// in a row, separated by FRAME_GAP
				//
				// However this fix does leave the potential for a small bug
				// I haven't seen this bug in practice but theoretically it could happen
				//  - the very last (or nearly last) chage message in a stream has a <frames> block in it
				//  - when we process it here, NONE of the frames are loaded so getFrames(frameIds) returns an empty array
				//  - we don't ever get to this block so we never centerCanvasAroundSceneBoundingBox
				//
				// The consequences of this are minor
				//  - Not zooming in is not the worst thing in the world, not destructire or anything
				// -  Usually there are multiple <frames> blocks in a chat response so the prior frames will have been zoomed into it
				// The fix for this is pretty gnarly
				//  - we have to store the <frames> blocks in some kind of queue that we process later, once the frames are loaded in the db
				//
				// So for now i'm going to do a simple hack and see if more problems arise

				// Instead of doing this:
				//    const bounds = frames.map((frame) => getFrameBounds(frame));
				// Instead take the first frame and use the # of frameIds, along with our frame gneneration
				// rules to generate the bounds.
				// This is robust to the situation where frames.length !== frameIds.length
				const firstFrameBounds = getFrameBounds(frames[0]);
				const bounds = frameIds.map((_, index) => {
					return {
						x: firstFrameBounds.x + index * (FRAME_GAP + firstFrameBounds.width),
						y: firstFrameBounds.y,
						width: firstFrameBounds.width,
						height: firstFrameBounds.height,
					};
				});
				const boundingBox = getBoundingBox(bounds);
				dispatch(
					centerCanvasAroundSceneBoundingBox({
						boundingBox,
						animated: true,
					})
				);
			}
		}
	}, [lastChatMessage, focusedFrameIds, centerFramesAsChatGeneratesThem, dispatch]);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------

	const onContextMenuInMessage = (e: React.MouseEvent) => {
		const selection = window.getSelection();
		const selectedText = selection?.toString() || null;

		if (selectedText) {
			const menuItems = [MenuItemAction.createCopyText(selectedText)];
			dispatch(
				showContextMenu({
					point: { x: e.clientX + 5, y: e.clientY },
					menuItems,
				})
			);
		}
	};

	const onDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDraggingFile(true);
	};

	const onDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDraggingFile(false);
	};

	// File upload callbacks using Redux dispatch
	const fileCallbacks = useFileUploadCallbacks({
		type: 'scene',
		chatId: activeChat?.id || null,
	});

	const onDrop = (e: React.DragEvent) => {
		setIsDraggingFile(false);

		if (!canEdit(scene)) {
			displayViewOnlyModal();
			return;
		}

		if (!activeChat) return;

		// Use the utility handler
		onFileDropped(fileCallbacks)(e);
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	const renderEntries = (entries: ChatEntry[]) => {
		return entries.map((entry, index) => {
			if (
				entry.kind === 'system' &&
				(!entry.restriction || entry.restriction === ChatEntryRestriction.USER)
			) {
				const parts = splitMessageWithFrames(entry.message);
				return (
					<S.SystemMessage
						key={index}
						className="system-message"
						isMobile={isMobile}
						onContextMenu={onContextMenuInMessage}
					>
						{parts.map((part, i) => {
							if (part.type === 'text') {
								const textPart = part as ChatMessageTextPart;
								return (
									<MarkdownText key={i} onContextMenu={onContextMenuInMessage}>
										{textPart.content}
									</MarkdownText>
								);
							} else {
								const framesPart = part as ChatMessageFramesPart;
								return (
									<S.Frames
										key={i}
										onWheel={(e) => {
											e.stopPropagation();
											e.currentTarget.scrollLeft += e.deltaX;
										}}
									>
										{framesPart.frameIds.map((frameId) => (
											<Thumbnail
												key={frameId}
												frameId={frameId}
												size={ThumbnailSize.CHAT}
												selectOnClick
											/>
										))}
									</S.Frames>
								);
							}
						})}
					</S.SystemMessage>
				);
			}
			if (entry.kind === 'user') {
				const userEntry = entry as UserChatEntry;
				return (
					<UserMessage
						key={index + 'user'}
						message={entry.message}
						isMobile={isMobile}
						attachments={userEntry.attachments}
						sourceFrameIds={userEntry.sourceFrameIds}
						onContextMenu={onContextMenuInMessage}
					/>
				);
			}
		});
	};

	const renderStatus = () => {
		if (!activeChat?.metadata?.statusMessage) return null;
		if (!activeChat?.metadata?.status) return null; // just in case...

		const { status, statusMessage } = activeChat.metadata;

		// Only show status for processing or stoppable states
		if (status !== ChatStatus.PROCESSING && status !== ChatStatus.STOPPABLE) {
			return null;
		}

		return (
			<S.StatusMessage isMobile={isMobile}>
				<S.StatusText>{statusMessage}</S.StatusText>
				<Loader width={16} height={16} />
			</S.StatusMessage>
		);
	};

	if (!activeChat) {
		return null;
	}

	return (
		<>
			<S.Base onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
				<S.ScrollingArea ref={containerRef} data-chat-scroll-container>
					{renderEntries(activeChat.entries)}
					{renderStatus()}
					<div ref={spacerRef} />
				</S.ScrollingArea>
				{isDraggingFile && (
					<S.DragOverlay>
						<S.DragOverlayContent>
							<Icon type={IconType.ATTACHMENT} width={32} height={32} color={theme.gray[1000]} />
							<S.DragOverlayText isMobile={isMobile}>Drop files to attach</S.DragOverlayText>
						</S.DragOverlayContent>
					</S.DragOverlay>
				)}
				{activeChat.entries.length > 0 && <S.Spacer />}
				<S.InputArea>
					{!isScrolledToBottom && (
						<S.JumpButton onClick={onJumpToBottom} isMobile={isMobile}>
							<S.JumpButtonIcon>
								<Icon
									type={IconType.LINE_ARROW_DOWN}
									width={isMobile ? 24 : 18}
									height={isMobile ? 24 : 18}
									color={theme.gray[700]}
								/>
							</S.JumpButtonIcon>
						</S.JumpButton>
					)}
					<SceneChatInput key={activeChat.id} chat={activeChat} />
				</S.InputArea>
			</S.Base>
		</>
	);
};

export default SceneConversation;
